/**
 * AI word explanation — Tap-to-Ask backing store.
 *
 * Real product would call an LLM here. For the demo we ship a static lookup
 * table covering ~30 frequent words, plus a fallback generator that produces
 * a reasonable-looking explanation from heuristics. The UI shows a small
 * spinner so it feels like a live call.
 */

export interface WordExplanation {
  word: string;
  pos: string;
  zh: string;
  ipa?: string;
  why: string;          // contextual "why this word here"
  collocations?: string[];
  examples?: { en: string; zh: string }[];
}

const LIB: Record<string, WordExplanation> = {
  am: { word: 'am', pos: 'aux. v.', zh: '是（be 动词第一人称单数）', ipa: '/æm/', why: '主语是 I，所以 be 动词用 am；用 is/are 都会语法错误。', collocations: ["I am", "I'm"] },
  is: { word: 'is', pos: 'aux. v.', zh: '是（be 动词第三人称单数）', ipa: '/ɪz/', why: '主语是 he / she / it 或单数名词时用 is。', collocations: ['it is', "she's"] },
  are: { word: 'are', pos: 'aux. v.', zh: '是（be 动词复数 / 第二人称）', ipa: '/ɑːr/', why: '主语是 you / we / they 或复数名词时用 are。', collocations: ["you're", "they're"] },
  a: { word: 'a', pos: 'art.', zh: '一个（不定冠词）', ipa: '/ə/', why: '后接以辅音音素开头的单数可数名词。后接元音音素时改为 an（an apple）。' },
  an: { word: 'an', pos: 'art.', zh: '一个（不定冠词）', ipa: '/ən/', why: '后接以元音音素开头的单数可数名词，如 an apple。' },
  the: { word: 'the', pos: 'art.', zh: '这个 / 那个（定冠词）', ipa: '/ðə/', why: '特指双方都知道的对象时使用。' },
  student: { word: 'student', pos: 'n.', zh: '学生', ipa: '/ˈstjuːdənt/', why: '可数名词，单数前需要冠词（a/the）。', examples: [{ en: 'I am a student.', zh: '我是学生。' }] },
  friend: { word: 'friend', pos: 'n.', zh: '朋友', ipa: '/frend/', why: '可数名词；my best friend = 最好的朋友。', collocations: ['best friend', 'close friend'] },
  go: { word: 'go', pos: 'v.', zh: '去', ipa: '/ɡəʊ/', why: '原形动词；第三人称单数现在时要变 goes。', collocations: ['go to', 'go shopping'] },
  goes: { word: 'goes', pos: 'v.', zh: '去（第三人称单数）', ipa: '/ɡəʊz/', why: '主语是 he/she/it 时，一般现在时动词要加 -es 或 -s。' },
  by: { word: 'by', pos: 'prep.', zh: '通过；乘（交通工具）', ipa: '/baɪ/', why: '表示交通方式时不加冠词：by subway / by bus / by car。', collocations: ['by car', 'by bus', 'by train'] },
  in: { word: 'in', pos: 'prep.', zh: '在……里 / 在……月份', ipa: '/ɪn/', why: '城市、国家、月份、年份用 in：in Shanghai, in May, in 2026。' },
  on: { word: 'on', pos: 'prep.', zh: '在……上 / 具体某天', ipa: '/ɒn/', why: '具体某一天用 on：on Monday, on May 1st。' },
  at: { word: 'at', pos: 'prep.', zh: '在……地点 / 时刻', ipa: '/æt/', why: '具体地点或时刻用 at：at home, at 3 pm。' },
  could: { word: 'could', pos: 'modal v.', zh: '能；可以（委婉）', ipa: '/kʊd/', why: '比 can 更礼貌的请求句式。Could you ...? 比 Can you ...? 更柔和。' },
  kindly: { word: 'kindly', pos: 'adv.', zh: '请；好心地', ipa: '/ˈkaɪndli/', why: '在请求句中加 kindly 让语气更得体，常见于商务邮件。', collocations: ['kindly let me know'] },
  share: { word: 'share', pos: 'v.', zh: '分享', ipa: '/ʃeə(r)/', why: 'share + 文档/信息 = 把……发给（某人）。', collocations: ['share with', 'share information'] },
  report: { word: 'report', pos: 'n. / v.', zh: '报告', ipa: '/rɪˈpɔːt/', why: '这里作名词，前面有 the 表示特指。' },
  hope: { word: 'hope', pos: 'v.', zh: '希望', ipa: '/həʊp/', why: 'I hope this email finds you well = 邮件英语开场固定表达，等同于"愿您一切顺利"。' },
  finds: { word: 'finds', pos: 'v.', zh: '发现；觉得（第三人称单数）', ipa: '/faɪndz/', why: "this email finds you well 是固定搭配；finds 加 s 是因为主语 'this email' 是第三人称单数。" },
  well: { word: 'well', pos: 'adv. / adj.', zh: '好；健康的', ipa: '/wel/', why: '这里作形容词，意思是"健康的、状态好的"。' },
  watched: { word: 'watched', pos: 'v.', zh: '看了（watch 的过去式）', ipa: '/wɒtʃt/', why: 'watch 是规则动词，过去式直接加 -ed。' },
  movie: { word: 'movie', pos: 'n.', zh: '电影', ipa: '/ˈmuːvi/', why: '美式英语；英式更常用 film。', collocations: ['watch a movie'] },
  last: { word: 'last', pos: 'adj.', zh: '上一个；最近过去的', ipa: '/lɑːst/', why: 'last night = 昨晚；last week = 上周；不要加 in/on（错：on last night）。' },
  resilience: { word: 'resilience', pos: 'n.', zh: '韧性；恢复力', ipa: '/rɪˈzɪliəns/', why: '常用于描述人或系统在逆境后恢复的能力。雅思口语写作高频词。', collocations: ['build resilience', 'mental resilience'] },
  inspires: { word: 'inspires', pos: 'v.', zh: '激励（第三人称单数）', ipa: '/ɪnˈspaɪəz/', why: '主语 Her resilience 是单数，动词加 -s。', collocations: ['inspires sb to do'] },
  everyone: { word: 'everyone', pos: 'pron.', zh: '每个人', ipa: '/ˈevriwʌn/', why: '尽管"每个人"看起来是复数概念，但 everyone 在语法上是单数，谓语动词要用单数形式。' },
};

/** Strip punctuation, lowercase. */
function normalize(raw: string): string {
  return raw.toLowerCase().replace(/^[^a-z']+|[^a-z']+$/g, '');
}

export async function explainWord(rawWord: string, sentence: string): Promise<WordExplanation> {
  const key = normalize(rawWord);

  // Simulate a touch of network latency so the spinner means something.
  await new Promise((r) => setTimeout(r, 320 + Math.random() * 280));

  const hit = LIB[key];
  if (hit !== undefined) return hit;

  // Fallback heuristic — generate something plausible.
  const guessPos = guessPartOfSpeech(key);
  return {
    word: key,
    pos: guessPos,
    zh: '（点击右下角"AI 深度解释"获取详细释义）',
    why: `这是句子 "${sentence}" 中的一个 ${guessPos}。它的具体含义和搭配，AI 助教正在加载更详细的解释……`,
  };
}

function guessPartOfSpeech(word: string): string {
  if (word.endsWith('ly')) return 'adv.';
  if (word.endsWith('ing') || word.endsWith('ed')) return 'v.';
  if (word.endsWith('tion') || word.endsWith('ness') || word.endsWith('ity')) return 'n.';
  if (word.endsWith('ful') || word.endsWith('ous') || word.endsWith('ive')) return 'adj.';
  return 'word';
}
